import { useContext, useEffect, useMemo, useState } from 'react';

import { useForm } from 'react-hook-form';
import type { AxiosError } from 'axios';

import { useBooleanState } from '@vyce/core/src/hooks';
import {
  getAllTeamMembersRequest,
  getTeamQualificationsRequest,
  getTeamsQualifications,
} from '@vyce/core/src/api/teams';
import { fetchTagsRequest } from '@vyce/core/src/api/handbook';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import { useTable } from '@vyce/core/src/hooks/useTable';
import { GetCompanyTeamsMembersDataRequest, TeamQualificationWidgetDTO } from '@vyce/core/src/api/types';
import { useInfiniteScroll } from '@vyce/core/src/hooks/useInfiniteScroll';
import { GRID_PAGE_SIZE } from '@vyce/core/src/constants';
import { useMainDashboardFilter } from '@vyce/core/src/hooks/useMainDashboardFilter';
import { useIsMounted } from '@vyce/core/src/hooks/useIsMounted';
import { Tag, TagGroups, TeamMember } from '@vyce/core/src/types';
import { formatSortModel } from '@vyce/core/src/utils/sorting';

import { Props, QualificationFilterFormValues, QualificationFilterUserOption } from '../types';
import {
  MATCH_MODES,
  MatchMode,
  defaultSelectedTeamOption,
  defaultTeamOption,
} from '../config';
import { getQualificationReport } from '../../../../api/reports/indext';
import { generateExcelFileLink } from '../../../../utils/generateFileLink';

export const useWidgetData = ({ selectedCompanyId, mainDashboardFilters, customTeamId }: Props) => {
  const { handleServerError } = useContext(NotificationContext);
  const isMounted = useIsMounted();
  const [qualificationsOptions, setQualificationsOptions] = useState<string[]>([]);
  const [jobTitleOptions, setJobTitleOptions] = useState<string[]>([]);
  const [filterUsersSelectOptions, setFilterUsersSelectOptions] = useState<QualificationFilterUserOption[]>(
    []
  );
  const [loading, setLoadingTrue, setLoadingFalse] = useBooleanState(false);
  const [selectedStatusIds, setSelectedStatusIds] = useState<string[]>([]);
  const [matchMode, setMatchMode] = useState<MatchMode>(MATCH_MODES.hasAny);
  const [exportLoading, setExportLoadingTrue, setExportLoadingFalse] = useBooleanState(false);
  const [isOpenFilterDialog, setFilterDialogOpen, setFilterDialogFalse] = useBooleanState(false);

  const { total, setTotal } = useTable({});
  const {
    isLoadingList,
    data,
    setNewData,
    setInitialLoadingStatusesFalse,
    setInitialLoadingStatusesTrue,
    resetLoadingList,
  } = useInfiniteScroll<TeamQualificationWidgetDTO>([]);

  const { selectedTeamOptions, unselectedTeamOptions, getTeamsForRequest } = useMainDashboardFilter({
    mainDashboardFilters,
    locationOption: defaultTeamOption,
    selectedLocationOption: defaultSelectedTeamOption,
  });

  const methods = useForm<QualificationFilterFormValues>({
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: {
      qualifications: [],
      teamId: defaultTeamOption.value,
      filterUsers: [],
      jobTitles: [],
    },
  });

  const { watch } = methods;
  const qualifications = watch('qualifications');
  const teamId = watch('teamId');
  const filterUsers = watch('filterUsers');
  const jobTitles = watch('jobTitles');

  const selectedTeamName = useMemo(() => {
    const all = [...selectedTeamOptions, ...unselectedTeamOptions];
    return all.find(t => t.value === teamId)?.name ?? '';
  }, [teamId, selectedTeamOptions, unselectedTeamOptions]);

  const qualificationsSelectOptions = useMemo(() => {
    if (!qualificationsOptions) return [];
    return qualificationsOptions;
  }, [qualificationsOptions]);

  const teamIdsForMemberSearch = useMemo(
    () => getTeamsForRequest(customTeamId || teamId),
    [customTeamId, teamId, getTeamsForRequest]
  );
  const userIds = filterUsers.length ? filterUsers.map(u => u.user_id) : null;
  // Status is not applicable when matching workers who hold none of the selected qualifications.
  const statusesForRequest = matchMode === MATCH_MODES.hasNone ? [] : selectedStatusIds;

  const exportQualifications = async () => {
    try {
      setExportLoadingTrue();
      const res = await getQualificationReport({
        companyId: selectedCompanyId,
        payload: {
          team_ids: getTeamsForRequest(customTeamId || teamId) || [],
          qualifications,
          match_mode: matchMode,
          statuses: statusesForRequest,
          user_ids: userIds,
          job_titles: jobTitles,
        },
      });
      generateExcelFileLink(res);
    } catch (error) {
      handleServerError(error);
    } finally {
      setExportLoadingFalse();
    }
  };

  const getWidgetSelectorsData = async () => {
    if (!selectedCompanyId) {
      return;
    }

    try {
      const qualificationsData = await getTeamQualificationsRequest(selectedCompanyId);
      if (!isMounted.current) return;
      setQualificationsOptions(qualificationsData.data.items);
    } catch (e) {
      handleServerError(e);
    }
  };

  const getTeamQualifications = async (offset: number = 0, signal?: AbortSignal) => {
    if (!selectedCompanyId) {
      return;
    }

    const itemStatusMap: Record<number, boolean> = setInitialLoadingStatusesFalse(offset, GRID_PAGE_SIZE);

    setLoadingTrue();
    try {
      const res = await getTeamsQualifications({
        companyId: selectedCompanyId,
        data: {
          limit: GRID_PAGE_SIZE,
          offset,
          team_ids: getTeamsForRequest(customTeamId || teamId),
          qualifications,
          match_mode: matchMode,
          statuses: statusesForRequest,
          user_ids: userIds,
          job_titles: jobTitles,
        },
        signal,
      });
      setTotal(res.data.count);
      setNewData({ newData: res.data.items, offset });
      setInitialLoadingStatusesTrue({ offset, pageSize: GRID_PAGE_SIZE, itemStatusMap });
      setLoadingFalse();
    } catch (e) {
      const err = e as AxiosError;
      if (err.name !== 'CanceledError') {
        handleServerError(err);
        setLoadingFalse();
      }
    }
  };

  const getDataForAIAnalytics = async () => {
    try {
      const res = await getTeamsQualifications({
        companyId: selectedCompanyId,
        data: {
          limit: 3000,
          offset: 0,
          team_ids: getTeamsForRequest(customTeamId || teamId),
          qualifications,
          match_mode: matchMode,
          statuses: statusesForRequest,
          user_ids: userIds,
          job_titles: jobTitles,
        },
      });
      return res.data.items;
    } catch (e) {
      handleServerError(e);
      return [];
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    getTeamQualifications(0, signal);

    return () => {
      controller.abort();
    };
  }, [
    selectedCompanyId,
    qualifications,
    selectedStatusIds,
    matchMode,
    teamId,
    selectedTeamOptions,
    unselectedTeamOptions,
    filterUsers,
    jobTitles,
  ]);

  useEffect(() => {
    getWidgetSelectorsData();
  }, [selectedCompanyId]);

  useEffect(() => {
    if (!selectedCompanyId) {
      return;
    }
    (async () => {
      try {
        const res = await fetchTagsRequest({ group: TagGroups.ROLE, short: false });
        if (!isMounted.current) return;
        setJobTitleOptions(res.data.items.map((t: Tag) => t.name));
      } catch (e) {
        handleServerError(e);
      }
    })();
  }, [selectedCompanyId, handleServerError]);

  useEffect(() => {
    if (!selectedCompanyId) {
      setFilterUsersSelectOptions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const requestData: GetCompanyTeamsMembersDataRequest = {
          offset: 0,
          limit: 500,
          substring: '',
          order_by: formatSortModel<TeamMember>([{ field: 'first_name', sort: 'asc' }]),
          team_ids: teamIdsForMemberSearch,
        };
        const { data } = await getAllTeamMembersRequest(selectedCompanyId, requestData);
        if (!cancelled) {
          setFilterUsersSelectOptions(
            data.items.map((m: any) => ({
              user_id: m.user_id,
              name: `${m.first_name} ${m.last_name}`.trim(),
            }))
          );
        }
      } catch (e) {
        if (!cancelled) {
          handleServerError(e);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedCompanyId, teamIdsForMemberSearch, handleServerError]);

  useEffect(() => {
    if (selectedTeamOptions.length) {
      methods.setValue('teamId', selectedTeamOptions[0].value);
    } else {
      methods.setValue('teamId', unselectedTeamOptions[0].value);
    }
  }, [selectedTeamOptions, unselectedTeamOptions]);

  return {
    teamQualifications: data,
    total,
    loading,
    selectedTeamOptions,
    unselectedTeamOptions,
    qualificationsSelectOptions,
    jobTitleOptions,
    filterUsersSelectOptions,
    selectedStatusIds,
    matchMode,
    setMatchMode,
    methods,
    isLoadingList,
    setSelectedStatusIds,
    resetLoadingList,
    getTeamQualifications,
    exportQualifications,
    exportLoading,
    getDataForAIAnalytics,
    isOpenFilterDialog,
    setFilterDialogOpen,
    setFilterDialogFalse,
    selectedTeamName,
  };
};
